"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getDatasets = getDatasets;
exports.getDistanceBetweenEnds = getDistanceBetweenEnds;
exports.getLinesBeginPoints = getLinesBeginPoints;
exports.getLinesEndPoints = getLinesEndPoints;
exports.isData = isData;
exports.prepareSrcData = prepareSrcData;
exports.prepareXScaleRange = prepareXScaleRange;
var _ramda = require("ramda");
var _types = require("../types");
var _convertTimezone = require("./convertTimezone");
/** Возвращает точки начал графиков
 * Если таковая найдется, для графика оценки вернет точку, которая по X совпадает с началом графика цены
 * Если не найдется, просто вернет начало графика оценки.
 * */
function getLinesBeginPoints(ctx) {
  const {
    offerPriceDataset,
    estimationPriceDataset
  } = getDatasets(ctx.chart.data.datasets);
  let offerPricePoint = null;
  let estimationPricePoint = null;
  if (offerPriceDataset) {
    offerPricePoint = offerPriceDataset.data[0];
  }
  if (estimationPriceDataset) {
    if (offerPricePoint) {
      const offerPricePointDate = offerPricePoint.date;
      estimationPricePoint = estimationPriceDataset.data.find(_ref => {
        let {
          date
        } = _ref;
        return date === offerPricePointDate;
      }) || null;
    } else {
      estimationPricePoint = estimationPriceDataset.data[0];
    }
  }
  return {
    offerPricePoint,
    estimationPricePoint
  };
}

/** Возвращает точки концов графиков */
function getLinesEndPoints(ctx) {
  const {
    offerPriceDataset,
    estimationPriceDataset
  } = getDatasets(ctx.chart.data.datasets);
  const offerPricePoint = offerPriceDataset ? (0, _ramda.last)(offerPriceDataset.data) : null;
  const estimationPricePoint = estimationPriceDataset ? (0, _ramda.last)(estimationPriceDataset.data) : null;
  return {
    offerPricePoint,
    estimationPricePoint
  };
}

/** Расстояние между точками по Y в процентном соотношении от всего диапазона */
function getDistanceBetweenEnds(ctx) {
  const {
    min,
    max
  } = ctx.chart.scales.y;
  const {
    offerPricePoint,
    estimationPricePoint
  } = getLinesEndPoints(ctx);
  if (!offerPricePoint || !estimationPricePoint) {
    return null;
  }
  return Math.abs((estimationPricePoint.price - offerPricePoint.price) / (max - min));
}
function getDatasets(datasets) {
  const namesMap = {
    [_types.EChartDatasetId.OfferPrice]: 'offerPriceDataset',
    [_types.EChartDatasetId.EstimatedPrice]: 'estimationPriceDataset',
    [_types.EChartDatasetId.HouseTrend]: 'houseTrendDataset',
    [_types.EChartDatasetId.MarketTrend]: 'marketTrendDataset',
    [_types.EChartDatasetId.DemandTrend]: 'demandTrendDataset',
    [_types.EChartDatasetId.SupplyTrend]: 'supplyTrendDataset'
  };
  return datasets.reduce((result, dataset) => {
    result[namesMap[dataset.id]] = dataset;
    return result;
  }, {});
}
function isData(data) {
  return Boolean(data) && !(0, _ramda.isEmpty)(data);
}
function prepareXScaleRange(props, options) {
  const {
    data: {
      dateMin,
      dateMax
    }
  } = props;
  if (options.convertToUTC) {
    return {
      dateMin: (0, _convertTimezone.convertTimezone)(dateMin),
      dateMax: (0, _convertTimezone.convertTimezone)(dateMax)
    };
  }
  return {
    dateMin,
    dateMax
  };
}
function prepareSrcData(props, options) {
  const result = {};
  const chartsData = (0, _ramda.pick)(['offerPrice', 'estimatedPrice', 'houseTrend', 'marketTrend', 'demandTrend', 'supplyTrend'], props.data);
  result.datasetsXRange = calculateDatasetsRange(chartsData);
  if (options.convertToUTC) {
    result.data = {
      offerPrice: mapDatasetConvertTimezone(chartsData.offerPrice),
      estimatedPrice: mapDatasetConvertTimezone(chartsData.estimatedPrice),
      houseTrend: mapTrendsDatasetConvertTimezone(chartsData.houseTrend),
      marketTrend: mapTrendsDatasetConvertTimezone(chartsData.marketTrend),
      demandTrend: mapTrendsDatasetConvertTimezone(chartsData.demandTrend, props.type),
      supplyTrend: mapTrendsDatasetConvertTimezone(chartsData.supplyTrend, props.type)
    };
  } else {
    result.data = chartsData;
  }
  return result;
}
function mapTrendsDatasetConvertTimezone(dataset, type) {
  if (!isData(dataset)) {
    return undefined;
  }
  if (type === _types.EChartTypes.SupplyDemand) {
    return dataset.map(_ref2 => {
      let {
        reportDate,
        value
      } = _ref2;
      return {
        value,
        reportDate: (0, _convertTimezone.convertTimezone)(reportDate)
      };
    });
  }
  return dataset.map(_ref3 => {
    let {
      reportDate,
      value,
      prettyValue
    } = _ref3;
    return {
      value,
      reportDate: (0, _convertTimezone.convertTimezone)(reportDate),
      prettyValue
    };
  });
}
function mapDatasetConvertTimezone(dataset) {
  if (!isData(dataset)) {
    return undefined;
  }
  return dataset.map(_ref4 => {
    let {
      date,
      price
    } = _ref4;
    return {
      price,
      date: (0, _convertTimezone.convertTimezone)(date)
    };
  });
}
function calculateDatasetsRange(data) {
  if (isChartData(data)) {
    const combinedData = [data.estimatedPrice, data.offerPrice].filter(Boolean).flat();
    return combinedData.reduce((result, datasetItem) => {
      if (datasetItem.price < result.min) {
        result.min = datasetItem.price;
      }
      if (datasetItem.price > result.max) {
        result.max = datasetItem.price;
      }
      return result;
    }, {
      min: Infinity,
      max: -Infinity
    });
  } else if (isTrendData(data)) {
    const combinedData = [data.houseTrend, data.marketTrend].filter(Boolean).flat();
    return combinedData.reduce((result, datasetItem) => {
      if (datasetItem.value < result.min) {
        result.min = datasetItem.value;
      }
      if (datasetItem.value > result.max) {
        result.max = datasetItem.value;
      }
      return result;
    }, {
      min: Infinity,
      max: -Infinity
    });
  } else if (isSupplyData(data)) {
    const combinedData = [data.demandTrend, data.supplyTrend].filter(Boolean).flat();
    return combinedData.reduce((result, datasetItem) => {
      if (datasetItem.value < result.min) {
        result.min = datasetItem.value;
      }
      if (datasetItem.value > result.max) {
        result.max = datasetItem.value;
      }
      return result;
    }, {
      min: Infinity,
      max: -Infinity
    });
  }
  return {
    min: Infinity,
    max: -Infinity
  };
}
function isChartData(data) {
  return 'offerPrice' in data || 'estimatedPrice' in data;
}
function isSupplyData(data) {
  return 'demandTrend' in data || 'supplyTrend' in data;
}
function isTrendData(data) {
  return 'houseTrend' in data || 'marketTrend' in data;
}